import { useQuery } from '@tanstack/react-query';
import { List, ListItem } from 'framework7-react';
import { FC } from 'react';

import pluralize from 'pluralize';

import API from '@api/API';

import { createScreenComponent } from '@utilities/createScreenComponent';

import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const CityStandingList: FC<{
  isLoading: boolean;
  standings: CityEngagementStanding[];
  onClick: (standing: CityEngagementStanding) => void;
}> = ({ isLoading, standings, onClick }) => {
  if (isLoading) {
    return (
      <div className="d-grid gap-2">
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
        <Skeleton height={40} />
      </div>
    );
  }

  return (
    <List
      dividersIos
      mediaList
      className="mt-2 mb-2"
      style={{ marginLeft: '-.5rem', marginRight: '-1rem' }}
    >
      {standings.map((listItem, i) => (
        <ListItem key={listItem.cityId} onClick={() => onClick(listItem)}>
          <div slot="title">
            <div className="d-flex gap-1 align-items-center">
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {listItem.cityName}
              </div>
            </div>
          </div>

          <div slot="footer">{`${listItem.entertainerCount} ${pluralize('entertainer', listItem.entertainerCount)}`}</div>

          <div slot="media">
            <div style={{ position: 'relative' }}>
              <strong
                className="d-block text-align-center"
                style={{ width: 36 }}
              >{`${i + 1}`}</strong>
            </div>
          </div>
          <div slot="after">
            <strong>{listItem.score ?? 0}</strong>
          </div>
        </ListItem>
      ))}
    </List>
  );
};

interface CityEngagementStanding {
  cityId: string;
  cityName: string;
  score: number;
  entertainerCount: number;
}

const useCityEngagementStandings = () => {
  const { data, isFetched } = useQuery({
    queryKey: ['cityEngagementStandings'],
    queryFn: () => {
      return API.get<{ standings: CityEngagementStanding[] }>(`/v1/city-engagement-standings`);
    },
  });

  return {
    areCityEngagementStandingsReady: isFetched,
    cityEngagementStandings: data?.standings || [],
  };
};

interface CityEngagementStandingsScreenProps {}

export const CityEngagementStandingsScreen =
  createScreenComponent<CityEngagementStandingsScreenProps>(() => {
    const { areCityEngagementStandingsReady, cityEngagementStandings } =
      useCityEngagementStandings();

    return (
      <Screen
        name="Entertainer Looks Swiper Screen"
        hideToolbar
        disableLocationPermissionGate
        headerProps={{
          title: 'Share Your Badge',
        }}
      >
        <Screen.Content maxWidth={425}>
          <TitleToolbar text="City Standings" />
          <CityStandingList
            isLoading={!areCityEngagementStandingsReady}
            standings={cityEngagementStandings}
            onClick={() => {}}
          />
        </Screen.Content>
      </Screen>
    );
  });
